<template>
    <div
        class="flashcard-card"
        :class="{ 'is-flipped': isFlipped }"
        tabindex="0"
        role="button"
        :aria-label="isFlipped ? 'Flashcard showing answer. Press Enter or Space to flip back.' : 'Flashcard showing question. Press Enter or Space to reveal answer.'"
        @click="$emit('flip')"
        @keydown.enter.prevent="$emit('flip')"
        @keydown.space.prevent="$emit('flip')"
    >
        <div class="flashcard-card-inner">
            <div class="flashcard-card-front" :class="{ 'has-image': imageUrl }">
                <span class="card-label">QUESTION</span>
                <PrimeImage
                    v-if="imageUrl && !imageError"
                    :src="imageUrl"
                    alt="Reference image"
                    preview
                    class="card-image"
                    image-class="card-image-inner"
                    :pt="{ image: { onerror: onImageError } }"
                    @click.stop
                />
                <div v-else-if="imageUrl && imageError" class="card-image-fallback" @click.stop>
                    <i class="uil uil-image-slash" />
                    <span>Image unavailable</span>
                </div>
                <div class="card-content-text">{{ front }}</div>
                <p class="flip-hint">Tap to reveal</p>
            </div>
            <div class="flashcard-card-back">
                <div class="card-back-accent" />
                <span class="card-label">ANSWER</span>
                <div class="card-content-text">{{ back }}</div>
                <p class="flip-hint">Tap to flip back</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import PrimeImage from 'primevue/image';
import { ref, watch } from 'vue';

const imageError = ref(false);

const props = defineProps({
    front: {
        type: String,
        required: true,
    },
    back: {
        type: String,
        required: true,
    },
    isFlipped: {
        type: Boolean,
        default: false,
    },
    imageUrl: {
        type: String,
        default: null,
    },
});

defineEmits(['flip']);

function onImageError() {
    imageError.value = true;
}

// Reset error state when navigating to a different card
watch(
    () => props.imageUrl,
    () => {
        imageError.value = false;
    }
);
</script>

<style lang="scss" scoped>
@import '~assets/scss/toolbox.scss';

.flashcard-card {
    perspective: 1000px;
    cursor: pointer;
    min-height: 320px;
    margin: 1rem 0;

    .flashcard-card-inner {
        position: relative;
        width: 100%;
        min-height: 320px;
        transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        transform-style: preserve-3d;
    }

    &.is-flipped .flashcard-card-inner {
        transform: rotateY(180deg);
    }

    .flashcard-card-front,
    .flashcard-card-back {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        min-height: 320px;
        backface-visibility: hidden;
        border-radius: 12px;
        padding: 2.5rem 2rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        box-shadow:
            0 2px 8px rgba(36, 43, 54, 0.08),
            0 0 0 1px rgba(36, 43, 54, 0.04);
        transition: box-shadow 0.2s ease;
    }

    &:hover .flashcard-card-front,
    &:hover .flashcard-card-back {
        box-shadow:
            0 4px 16px rgba(36, 43, 54, 0.12),
            0 0 0 1px rgba(36, 43, 54, 0.06);
    }

    .flashcard-card-front {
        background: $white;
        border-left: 2px solid $primary;
    }

    .flashcard-card-back {
        background: $grey-lightest;
        transform: rotateY(180deg);
        text-align: left;
        align-items: flex-start;
    }

    .card-back-accent {
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        width: 2px;
        background: $primary;
        border-radius: 0 12px 12px 0;
    }

    .card-label {
        position: absolute;
        top: 1.25rem;
        left: 2rem;
        font-size: 0.7rem;
        font-weight: 600;
        letter-spacing: 0.1em;
        color: $grey;
    }

    .flashcard-card-front.has-image {
        padding-top: 3rem;
        gap: 0.75rem;
    }

    .card-image {
        margin-bottom: 0.5rem;
        cursor: zoom-in;

        :deep(.card-image-inner) {
            max-height: 200px;
            max-width: 100%;
            object-fit: contain;
            border-radius: 8px;
            border: 1px solid $grey-lighter;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        }
    }

    .card-image-fallback {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        width: 100%;
        max-width: 200px;
        height: 120px;
        margin-bottom: 0.5rem;
        border-radius: 8px;
        border: 1px dashed $grey-lighter;
        background: $grey-lightest;
        color: $grey-light;
        font-size: 0.85rem;

        i {
            font-size: 1.5rem;
        }
    }

    .card-content-text {
        font-size: 1.25rem;
        line-height: 1.7;
        max-width: 90%;
        color: $black;
    }

    .flashcard-card-back .card-content-text {
        font-size: 1.1rem;
        line-height: 1.75;
        max-width: 100%;
        padding: 0 0.5rem;
    }

    .flip-hint {
        position: absolute;
        bottom: 1.25rem;
        font-size: 0.8rem;
        color: $grey-light;
        transition: opacity 0.3s ease;
    }
}

@media (max-width: 768px) {
    .flashcard-card {
        min-height: 260px;

        .flashcard-card-inner {
            min-height: 260px;
        }

        .flashcard-card-front,
        .flashcard-card-back {
            min-height: 260px;
            padding: 2rem 1.25rem;
        }

        .card-content-text {
            font-size: 1.1rem;
        }

        .flashcard-card-back .card-content-text {
            font-size: 1rem;
        }

        .card-image {
            :deep(.card-image-inner) {
                max-height: 150px;
            }
        }
    }
}

@media (max-width: 576px) {
    .flashcard-card {
        min-height: 220px;

        .flashcard-card-inner {
            min-height: 220px;
        }

        .flashcard-card-front,
        .flashcard-card-back {
            min-height: 220px;
            padding: 1.75rem 1rem;
        }

        .card-content-text {
            font-size: 1rem;
        }

        .flip-hint {
            font-size: 0.75rem;
        }
    }
}
</style>
